<template>
  <validation-observer
    ref="syllabusStructureRef"
    #default="{invalid}"
  >
    <b-card>
      <div
        v-if="syllabusStructure.length === 0"
        class="text-center text-muted py-4"
      >
        Không có dữ liệu để hiển thị.
      </div>

      <b-table
        v-else
        :items="syllabusStructure"
        :fields="generalScoreFields"
        bordered
        responsive
        small
      >
        <template #cell(index)="data">
          {{ data.index + 1 }}
        </template>

        <template #cell(generalScoreName)="{ item }">
          <validation-provider
            #default="{ errors }"
            name="Điểm chung"
            rules="required"
          >
            <v-select
              v-model="item.generalScoreId"
              label="name"
              placeholder="Chọn điểm chung"
              :reduce="option => option.id"
              :options="generalScoreOptions"
              :class="{ 'is-invalid': errors.length > 0 }"
              @input="onGeneralScoreChange(item)"
            />
          </validation-provider>
        </template>

        <template #cell(generalWeight)="{ item }">
          <b-form-input
            v-model.number="item.generalWeight"
            type="number"
            step="0.01"
            min="0"
            max="1"
            class="text-center"
            placeholder="0.0"
          />
        </template>

        <template #cell(components)="{ item }">
          <b-table
            :items="item.components"
            :fields="componentScoreFields"
            small
            responsive
            bordered
          >
            <template #cell(componentScoreName)="{ item: comp }">
              <validation-provider
                #default="{ errors }"
                name="Điểm thành phần"
                rules="required"
              >
                <v-select
                  v-model="comp.componentScoreId"
                  label="name"
                  :options="componentScoreOptionsMap[item.generalScoreId] || []"
                  :reduce="option => option.id"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  placeholder="Chọn điểm thành phần"
                />
              </validation-provider>
            </template>
            <template #cell(actions)="{ item: comp, index: i }">
              <b-button
                size="sm"
                variant="outline-danger"
                @click="removeComponent(item, i)"
              >
                Xoá
              </b-button>
            </template>
          </b-table>
          <b-button
            size="sm"
            class="mt-1"
            variant="outline-primary"
            @click="addComponent(item)"
          >
            + Thêm điểm thành phần
          </b-button>
        </template>

        <template #cell(actions)="{ item }">
          <b-button
            size="sm"
            variant="outline-danger"
            @click="removeGeneralScore(item)"
          >
            Xoá
          </b-button>
        </template>
      </b-table>

      <b-button
        variant="outline-primary"
        class="mt-2"
        @click="addGeneralScore"
      >
        + Thêm hệ số điểm chung
      </b-button>

      <div class="w-100 d-flex justify-content-end mt-2">
        <b-button
          v-if="creatable"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          class="mr-1"
          :disabled="invalid"
          @click="saveStructure"
        >
          <span class="text-right">
            <feather-icon icon="CheckIcon" /> Lưu lại
          </span>
        </b-button>

        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="outline-secondary"
          @click="$bvModal.hide('syllabusStructureModal')"
        >
          <span class="text-right">
            <feather-icon icon="XIcon" /> Hủy
          </span>
        </b-button>
      </div>
      <b-overlay
        no-wrap
        variant="white"
        spinner-variant="primary"
        blur="0"
        opacity=".75"
        :show="isLoading"
      />
    </b-card>
  </validation-observer>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import {
  BButton, BCard, BFormInput, BOverlay, BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { hasPermissionForResource, isSystemAdmin } from '@/utils'
import { PermissionCode, ResourceCode } from '@/const/code'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import api from './api'

export default {
  name: 'SyllabusStructure',
  directives: {
    Ripple,
  },
  components: {
    ValidationProvider,
    BOverlay,
    BCard,
    BTable,
    BButton,
    BFormInput,
    vSelect,
    ValidationObserver,
  },
  props: {
    syllabus: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      isLoading: false,
      generalScoreOptions: [],
      componentScores: [],
      componentScoreOptionsMap: {},
      syllabusStructure: [],
      generalScoreFields: [
        { key: 'index', label: 'STT', class: 'text-center' },
        { key: 'generalScoreName', label: 'Tên điểm chung', class: 'text-center' },
        { key: 'generalWeight', label: 'Trọng số', class: 'text-center' },
        { key: 'components', label: 'Điểm thành phần', class: 'text-center' },
        { key: 'actions', label: 'Thao tác', class: 'text-center' },
      ],
      componentScoreFields: [
        { key: 'componentScoreName', label: 'Tên điểm thành phần' },
        { key: 'actions', label: 'Thao tác', class: 'text-center' },
      ],
    }
  },
  computed: {
    creatable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.CREATE, ResourceCode.SYLLABUS_STRUCTURE)
    },
    updatable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.UPDATE, ResourceCode.SYLLABUS_STRUCTURE)
    },
    deletable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.DELETE, ResourceCode.SYLLABUS_STRUCTURE)
    },
  },
  async created() {
    this.isLoading = true
    try {
      const [syllabusStructureRes, generalScoreOptionRes] = await Promise.all([
        api.getSyllabusStructure({ syllabusId: this.syllabus.id }),
        api.getGeneralScores(),
      ])
      this.syllabusStructure = syllabusStructureRes
      this.generalScoreOptions = generalScoreOptionRes
      await Promise.all(
        this.syllabusStructure.map(item => {
          if (item.generalScoreId) {
            return this.onGeneralScoreChange(item, true)
          }
          return null
        }),
      )
    } catch (e) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: `[${e.code}] ${e.message}`,
          icon: 'XCircleIcon',
          variant: 'danger',
        },
      })
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    addGeneralScore() {
      this.syllabusStructure.push({
        generalScoreId: null,
        generalScoreName: '',
        generalWeight: 0,
        components: [],
      })
    },
    removeGeneralScore(item) {
      const idx = this.syllabusStructure.indexOf(item)
      if (idx !== -1) this.syllabusStructure.splice(idx, 1)
    },
    addComponent(generalScore) {
      generalScore.components.push({
        componentScoreId: null,
        componentScoreName: '',
      })
    },
    removeComponent(generalScore, index) {
      generalScore.components.splice(index, 1)
    },
    async onGeneralScoreChange(item, isInit = false) {
      if (!item.generalScoreId) {
        return
      }
      try {
        if (!isInit) {
          item.components = [{
            componentScoreId: null,
            componentScoreName: null,
          }]
        }
        this.componentScores = await api.getComponentScoresByGeneralScoreId({ generalScoreId: item.generalScoreId })
        this.$set(this.componentScoreOptionsMap, item.generalScoreId, [...this.componentScores])
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `[${e.code}] ${e.message}`,
            icon: 'XCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    getElementState(errors) {
      return errors.length > 0 ? false : null
    },
    async saveStructure() {
      const valid = this.$refs
        .syllabusStructureRef
        .validate()
      if (valid) {
        try {
          const totalWeight = this.syllabusStructure.reduce((sum, item) => sum + Number(item.generalWeight || 0), 0)
          if (totalWeight < 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Tổng trọng số không hợp lệ',
                text: `Tổng trọng số không được âm. Hiện tại: ${totalWeight.toFixed(2)}`,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
            return
          }
          if (Math.abs(totalWeight - 1) > 0.001) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Tổng trọng số các hệ số điểm chung phải bằng 1',
                text: `Hiện tại: ${totalWeight.toFixed(2)}`,
                icon: 'AlertTriangleIcon',
                variant: 'warning',
              },
            })
            return
          }
          this.isLoading = true
          const param = {
            syllabusId: this.syllabus.id,
            generalWeights: this.syllabusStructure.map(group => ({
              generalScoreId: group.generalScoreId,
              weight: group.generalWeight,
              components: group.components.map(comp => ({
                componentScoreId: comp.componentScoreId,
              })),
            })),
          }
          await api.saveSyllabusStructure(param)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Cấu hình đề cương thành công!',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('succeed')
          this.$bvModal.hide('syllabusStructureModal')
        } catch (error) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Lỗi khi lưu',
              text: error?.message || 'Vui lòng thử lại',
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          })
        } finally {
          this.isLoading = false
        }
      }
    },
  },
}
</script>
<style>
.vs__dropdown-menu {
  z-index: 9999 !important;
  position: absolute !important;
}
.is-invalid .vs__dropdown-toggle {
  border-color: #ea5455;
}
.table-responsive {
  overflow: visible !important;
}
</style>
